import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ScrollView as ScrollViewNative } from "react-native";
import { useRouter } from "solito/router";
import type { TamaguiElement } from "tamagui";
import { Settings as SettingsIcon } from "@tamagui/lucide-icons";
import { AlertBar, Button, isWeb, scrollToNode, Theme, YStack } from "@medbillai/ui";
import { settingsUrl } from "@medbillai/utils";
import { ScreenView, type ScreenViewProps } from "../../../components/ScreenView";
import { analyticsService } from "../../../lib/analytics";
import type { HomeScreenViewedVariant } from "../../../lib/analytics/types";
import { errorHandling } from "../../../lib/apollo/utils";
import { isSaveTargeted, useAuth } from "../../../lib/auth";
import { ReferralModal } from "../../auth/components/ReferralModal";
import { useFlaggedEobsCount } from "../../bill/utils/useFlaggedEobs";
import { AttentionAlert } from "../sections/AttentionAlert";
import { GreetingHeader } from "../sections/GreetingHeader";
import { PrimaryCtaZone } from "../sections/PrimaryCtaZone";
import { QuickStartSection } from "../sections/QuickStartSection";
import { useClosedWin } from "../sections/useClosedWin";
import { useYourCases } from "../sections/useYourCases";
import { YourCasesSection } from "../sections/YourCasesSection";
import { checkLinksQuery, getPersonalizedPromptsQuery } from "../utils/queries";
import { useHomeScreenViewedEvent } from "./useHomeScreenViewedEvent";
export type HomeContentProps = {
  screenViewProps?: Omit<ScreenViewProps, "children">;
  // Experiment-arm tag for HomeScreenViewed; computed by HomeScreen from the flag
  // evaluation detail so it reflects experiment membership, not just which UI rendered.
  variant: HomeScreenViewedVariant;
};

// Cases Home reads wider than the default screen cap (768px content width).
const HOME_MAX_WIDTH = 768;
export function HomeContent({
  screenViewProps,
  variant
}: HomeContentProps) {
  const {
    push
  } = useRouter();
  const {
    isAuthenticated,
    isReady,
    user
  } = useAuth();
  const {
    data: linksData,
    loading: linksLoading,
    error: linksError,
    refetch: refetchLinks
  } = useQuery(checkLinksQuery, {
    context: errorHandling("caller"),
    skip: !isAuthenticated || !isReady
  });
  const {
    data: promptsData,
    loading: promptsLoading,
    error: promptsError,
    refetch: refetchPrompts
  } = useQuery(getPersonalizedPromptsQuery, {
    context: errorHandling("caller"),
    skip: !isAuthenticated || !isReady
  });
  const {
    count: flaggedEobCount,
    loading: flaggedEobsLoading,
    error: flaggedEobsError,
    refetch: refetchFlaggedEobs
  } = useFlaggedEobsCount();
  const {
    cases: yourCases,
    totalCount: yourCasesTotal,
    blockedCases,
    blockedCount,
    loading: yourCasesLoading,
    error: yourCasesError,
    refetch: refetchYourCases
  } = useYourCases();
  const {
    primaryWin,
    demotedWins,
    loading: closedWinLoading,
    error: closedWinError,
    refetch: refetchClosedWin
  } = useClosedWin();

  // The attention alert scrolls the page to the "Your cases" blocked band when
  // several cases need attention (within the list cap).
  const scrollRef = useRef<ScrollViewNative | null>(null);
  const yourCasesRef = useRef<TamaguiElement | null>(null);
  const scrollToYourCases = useCallback(() => {
    scrollToNode(yourCasesRef, scrollRef, {
      block: "start"
    });
  }, []);
  const handleRefresh = useCallback(async () => {
    // allSettled: one dead endpoint must not abort the others or reject out of
    // the pull-to-refresh handler.
    await Promise.allSettled([refetchLinks(), refetchPrompts(), refetchFlaggedEobs(), refetchYourCases(), refetchClosedWin()]);
  }, [refetchLinks, refetchPrompts, refetchFlaggedEobs, refetchYourCases, refetchClosedWin]);
  const [retrying, setRetrying] = useState(false);
  const retryLoad = useCallback(async () => {
    setRetrying(true);
    try {
      await handleRefresh();
    } finally {
      setRetrying(false);
    }
  }, [handleRefresh]);
  const handleSettingsPress = useCallback(() => {
    analyticsService.trackEvent("ClickedSettings", {});
    push(settingsUrl());
  }, [push]);

  // The redesign hides the native header (and its settings button) — see the
  // shared `(home)/(tabs)/home/_layout.tsx` — so this screen owns the top-right
  // settings affordance on native. Web keeps settings in its top bar.
  const showsNativeSettingsFallback = !isWeb;
  const insuranceLinked = (linksData?.me?.flexpaLinks?.some(link => link.active) ?? false) || (linksData?.me?.webAgentLinks?.some(link => link.active) ?? false);

  // Everyone leads with bill help except the save-intent segment, who lead
  // with bill monitoring; a closed-win outranks both.
  const ctaLayout = {
    intent: isSaveTargeted(user?.userSegmentationData?.features) ? "insurance" as const : "bill" as const,
    // A failed links query means status unknown, not unlinked — arbitration
    // then drops both link-dependent heroes rather than pitch the wrong one.
    insuranceLinked: linksError ? undefined : insuranceLinked,
    newClaimsCount: flaggedEobCount,
    closedWin: primaryWin ?? undefined
  };
  const loadError = linksError ?? promptsError ?? flaggedEobsError ?? yourCasesError ?? closedWinError;
  const loading = linksLoading || promptsLoading || flaggedEobsLoading || yourCasesLoading || closedWinLoading;
  useHomeScreenViewedEvent({
    loading,
    variant,
    casesShownCount: yourCasesTotal,
    actionableCasesCount: blockedCount,
    hasClosedWin: primaryWin != null
  });
  const loadFailedFired = useRef(false);
  useEffect(() => {
    if (loading || !loadError || loadFailedFired.current) {
      return;
    }
    loadFailedFired.current = true;
    const failedQueries = [linksError && "links", promptsError && "prompts", flaggedEobsError && "flaggedEobs", yourCasesError && "yourCases", closedWinError && "closedWin"].filter((query): query is string => Boolean(query));
    analyticsService.trackEvent("HomeLoadFailed", {
      failedQueries
    });
  }, [loading, loadError, linksError, promptsError, flaggedEobsError, yourCasesError, closedWinError]);
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="HomeContent" data-sentry-source-file="HomeContent.tsx">
      <ScreenView noPadding centerHorizontal backgroundRadialGradient backgroundRadialGradientProps={{
      backgroundColor: "$surface1"
    }} onRefresh={handleRefresh} widthLimit={HOME_MAX_WIDTH} {...screenViewProps} scrollViewProps={{
      ref: scrollRef,
      ...screenViewProps?.scrollViewProps
    }} stackProps={{
      gap: 0
    }} data-sentry-element="ScreenView" data-sentry-source-file="HomeContent.tsx">
        {/* Uniform 32px gap between sections + 16px horizontal inset; sections
            own no outer padding. Sections own their loading state (leaf-swap
            skeletons through the real layout). paddingTop drops to $sp2 when
            the settings button sits in-flow above the greeting. */}
        <YStack gap="$sp4" paddingHorizontal="$sp2" paddingTop={showsNativeSettingsFallback ? "$sp2" : "$sp10"} paddingBottom="$sp6" data-sentry-element="YStack" data-sentry-source-file="HomeContent.tsx">
          {/* Web already has settings access via the top bar. */}
          {showsNativeSettingsFallback ? <Button variant="secondary" size="small" icon={SettingsIcon} aria-label="Settings" alignSelf="flex-end" onPress={handleSettingsPress} /> : null}
          <GreetingHeader loading={loading} data-sentry-element="GreetingHeader" data-sentry-source-file="HomeContent.tsx" />
          {loadError && !loading ? <AlertBar testID="home.load-error" status="negative" title="Couldn't load everything" message="Check your connection and retry." button={{
          variant: "secondary",
          size: "small",
          fillWidth: false,
          children: "Retry",
          disabled: retrying,
          onPress: retryLoad
        }} /> : null}
          {/* Alert + CTA are one grouped section (tighter 16px internal gap)
              before the 32px gap to "Your cases". */}
          <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="HomeContent.tsx">
            {loading ? null : <AttentionAlert blockedCases={blockedCases} onScrollToBlocked={scrollToYourCases} />}
            <PrimaryCtaZone loading={loading} layout={ctaLayout} data-sentry-element="PrimaryCtaZone" data-sentry-source-file="HomeContent.tsx" />
          </YStack>
          <YourCasesSection loading={loading} cases={yourCases} totalCount={yourCasesTotal} demotedWins={demotedWins} containerRef={yourCasesRef} data-sentry-element="YourCasesSection" data-sentry-source-file="HomeContent.tsx" />
          <QuickStartSection loading={loading} prompts={promptsData?.getPersonalizedPrompts?.prompts ?? []} data-sentry-element="QuickStartSection" data-sentry-source-file="HomeContent.tsx" />
        </YStack>
        <ReferralModal data-sentry-element="ReferralModal" data-sentry-source-file="HomeContent.tsx" />
      </ScreenView>
    </Theme>;
}